import { useCallback, useEffect, useState } from "react";
import { useAuth } from "wasp/client/auth";
import { getLiteModeAccess, useQuery } from "wasp/client/operations";
import { canUseAdvancedMode, parseUiMode, resolveUiMode, type UiMode } from "./uiMode";

// Phase de test : la préférence vit dans le navigateur, par utilisateur. Avant
// preprod elle passera dans une colonne User.uiMode (migration additive).
const keyFor = (userId: string) => `edgely:uiMode:${userId}`;
const introKeyFor = (userId: string) => `edgely:liteIntroSeen:${userId}`;
const CHANGE_EVENT = "edgely:uimode-change";

function read(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function write(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* stockage indisponible */ }
}

export interface UiModeState {
  /** L'utilisateur a-t-il accès au Lite (LITE_MODE_USERS) ? Sinon : l'app actuelle, sans changement. */
  enabled: boolean;
  /**
   * L'accès est-il connu ? Les pages aiguillées attendent ce signal : rendre
   * l'Advanced « en attendant » le montait réellement, avec ses effets serveur
   * (ex. création du layout par défaut des sessions), même pour un utilisateur Lite.
   */
  ready: boolean;
  mode: UiMode;
  canUseAdvanced: boolean;
  setMode: (m: UiMode) => void;
  introSeen: boolean;
  markIntroSeen: () => void;
}

export function useUiMode(): UiModeState {
  const { data: user } = useAuth();
  const userId = user?.id ?? null;
  // Accès décidé côté serveur. Tant que la réponse n'est pas arrivée : Advanced,
  // pour qu'un utilisateur non autorisé ne voie jamais le Lite, même un instant.
  const access = useQuery(getLiteModeAccess, undefined, { enabled: !!userId, staleTime: Infinity });
  const enabled = !!access.data?.enabled;
  const ready = !!userId && !access.isLoading;
  const [, force] = useState(0);

  // Tous les composants qui lisent le mode se resynchronisent à chaque bascule.
  useEffect(() => {
    const onChange = () => force(n => n + 1);
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const planUser = (user ?? {}) as { subscriptionPlan?: string | null; subscriptionStatus?: string | null; paymentFailedAt?: Date | string | null };
  const preference = userId ? parseUiMode(read(keyFor(userId))) : null;
  const canUseAdvanced = canUseAdvancedMode(planUser);
  const mode: UiMode = !enabled ? "advanced" : resolveUiMode(planUser, preference);

  const setMode = useCallback((m: UiMode) => {
    if (!userId) return;
    write(keyFor(userId), m);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, [userId]);

  const introSeen = !userId || read(introKeyFor(userId)) === "1";
  const markIntroSeen = useCallback(() => {
    if (!userId) return;
    write(introKeyFor(userId), "1");
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, [userId]);

  return { enabled, ready, mode, canUseAdvanced, setMode, introSeen, markIntroSeen };
}
